import React, { useState, useEffect } from 'react';
import api from '../services/api';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const BillPaymentChecklist = ({ onClose }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [allLocations, setAllLocations] = useState([]);
  const [paymentModal, setPaymentModal] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [addBillModal, setAddBillModal] = useState(false);
  const [newBillLocationId, setNewBillLocationId] = useState('');
  const [newBillName, setNewBillName] = useState('');
  const [newBillVendor, setNewBillVendor] = useState('');
  const [newBillFrequency, setNewBillFrequency] = useState('monthly');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState('');
  const [showCloseMonth, setShowCloseMonth] = useState(false);
  const [monthSummary, setMonthSummary] = useState(null);
  const [monthClosed, setMonthClosed] = useState(false);

  useEffect(() => {
    loadData();
  }, [selectedYear, selectedMonth]);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await api.getBillPayments(selectedYear, selectedMonth);
      setLocations(data);
      const locs = await api.getBillLocations();
      setAllLocations(locs);
      const banks = await api.getBankAccounts();
      setBankAccounts(banks);
      // Check if month is closed
      try {
        const closure = await api.getMonthClosure(selectedYear, selectedMonth);
        setMonthClosed(!!closure?.closed_at);
      } catch { setMonthClosed(false); }
    } catch (err) {
      console.error('Failed to load bill payments:', err);
    }
    setLoading(false);
  };

  const handleCloseMonth = async () => {
    try {
      const summary = await api.getMonthSummary(selectedYear, selectedMonth);
      setMonthSummary(summary);
      setShowCloseMonth(true);
    } catch (err) {
      alert('Failed to load summary: ' + err.message);
    }
  };

  const confirmCloseMonth = async () => {
    try {
      await api.closeMonth(selectedYear, selectedMonth, monthSummary);
      setShowCloseMonth(false);
      setMonthClosed(true);
      alert(`${MONTHS[selectedMonth - 1]} ${selectedYear} has been closed!`);
    } catch (err) {
      alert('Failed to close month: ' + err.message);
    }
  };

  const handleBackfillSaleDates = async () => {
    if (!window.confirm('This will set sale_date for all deals missing it. Deals created in February 2026 will be set to January 31, 2026. Continue?')) {
      return;
    }
    try {
      const result = await api.backfillSaleDates();
      alert(`✅ ${result.message}\n\nStats:\n- Total updated: ${result.stats.totalUpdated}\n- Moved to January: ${result.stats.movedToJanuary}\n- Used created_at: ${result.stats.usedCreatedAt}`);
    } catch (err) {
      alert('Failed to backfill sale dates: ' + err.message);
    }
  };

  const handleToggleBill = (bill) => {
    if (bill.isPaid) {
      // Unchecking - just toggle without modal
      confirmToggle(bill.id, false, null, null);
    } else {
      // Checking - show modal to enter amount
      setPaymentModal(bill);
      setPaymentAmount(bill.typicalAmount > 0 ? bill.typicalAmount.toString() : '');
      setPaymentNotes('');
      setSelectedBankAccount('');
    }
  };

  const confirmToggle = async (billTypeId, isPaid, amount, notes) => {
    try {
      await api.toggleBillPayment({
        bill_type_id: billTypeId,
        year: selectedYear,
        month: selectedMonth,
        is_paid: isPaid,
        paid_amount: amount ? parseFloat(amount) : null,
        notes: notes || null,
        bank_account_id: selectedBankAccount ? parseInt(selectedBankAccount) : null,
      });
      setPaymentModal(null);
      loadData();
    } catch (err) {
      alert('Failed to update: ' + err.message);
    }
  };

  const handleAddBill = async () => {
    if (!newBillLocationId || !newBillName) {
      alert('Please select a location and enter a bill name');
      return;
    }
    try {
      await api.createBillType({
        location_id: parseInt(newBillLocationId),
        name: newBillName,
        vendor: newBillVendor,
        frequency: newBillFrequency,
      });
      setAddBillModal(false);
      setNewBillLocationId('');
      setNewBillName('');
      setNewBillVendor('');
      setNewBillFrequency('monthly');
      loadData();
    } catch (err) {
      alert('Failed to add bill: ' + err.message);
    }
  };

  const totalBills = locations.reduce((sum, loc) => sum + loc.bills.filter(b => b.isDueThisMonth).length, 0);
  const paidBills = locations.reduce((sum, loc) => sum + loc.bills.filter(b => b.isDueThisMonth && b.isPaid).length, 0);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white w-[1000px] max-h-[90vh] rounded-lg shadow-xl flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-lg">Bill Payment Checklist</h2>
            <p className="text-sm text-gray-500">Track monthly bill payments for each location</p>
          </div>
          <div className="flex items-center gap-4">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="border rounded px-3 py-1.5"
            >
              {MONTHS.map((m, i) => (
                <option key={i} value={i + 1}>{m}</option>
              ))}
            </select>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="border rounded px-3 py-1.5"
            >
              {[2024, 2025, 2026].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <button
              onClick={() => setAddBillModal(true)}
              className="px-3 py-1.5 rounded text-sm bg-green-100 text-green-700 hover:bg-green-200"
            >
              + Add Bill
            </button>
            <button
              onClick={handleBackfillSaleDates}
              className="px-3 py-1.5 rounded text-sm bg-purple-600 text-white hover:bg-purple-700"
              title="Set sale_date for all deals missing it"
            >
              Backfill Sale Dates
            </button>
            {!monthClosed ? (
              <button
                onClick={handleCloseMonth}
                className="px-3 py-1.5 rounded text-sm bg-blue-600 text-white hover:bg-blue-700"
              >
                Close Month
              </button>
            ) : (
              <span className="px-3 py-1.5 rounded text-sm bg-green-100 text-green-700">
                Month Closed
              </span>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="px-4 py-2 bg-gray-50 border-b">
          <div className="flex items-center justify-between text-sm mb-1">
            <span>{paidBills} of {totalBills} bills paid for {MONTHS[selectedMonth - 1]}</span>
            <span className="font-medium">{totalBills > 0 ? Math.round((paidBills / totalBills) * 100) : 0}%</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-green-500 transition-all"
              style={{ width: `${totalBills > 0 ? (paidBills / totalBills) * 100 : 0}%` }}
            />
          </div>
        </div>

        {/* Content - Compact 4-column grid */}
        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : (
            <>
              {/* All Bills Grid - 4 columns */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                {locations.map(location => (
                  <div key={location.id} className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-100 px-2 py-1.5 font-medium text-sm flex items-center justify-between">
                      <span>{location.name}</span>
                      <span className="text-xs text-gray-500">
                        {location.bills.filter(b => b.isPaid).length}/{location.bills.length}
                      </span>
                    </div>
                    <div className="divide-y">
                      {location.bills.map(bill => (
                        <div
                          key={bill.id}
                          className={`flex items-center gap-2 px-2 py-1.5 text-sm ${bill.isPaid ? 'bg-green-50' : ''} ${bill.frequency !== 'monthly' ? 'bg-orange-50' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={bill.isPaid}
                            onChange={() => handleToggleBill(bill)}
                            className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className={`truncate ${bill.isPaid ? 'line-through text-gray-400' : ''}`}>
                              {bill.name}
                              {bill.frequency !== 'monthly' && (
                                <span className="ml-1 text-xs text-orange-600">({bill.frequency})</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-400 truncate">{bill.vendor}</div>
                          </div>
                          {bill.isPaid && bill.paidAmount && (
                            <div className="text-xs text-green-600 font-medium flex-shrink-0">
                              ${bill.paidAmount.toFixed(0)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Payment Modal */}
        {paymentModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-96">
              <h3 className="font-semibold text-lg mb-4">
                Mark as Paid: {paymentModal.name}
              </h3>
              <p className="text-sm text-gray-500 mb-4">{paymentModal.vendor}</p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Amount Paid *</label>
                <input
                  type="number"
                  step="0.01"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Enter amount"
                  autoFocus
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Paid From *</label>
                <select
                  value={selectedBankAccount}
                  onChange={(e) => setSelectedBankAccount(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">Select account...</option>
                  {bankAccounts.map(acc => (
                    <option key={acc.id} value={acc.id}>{acc.name}</option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Notes (optional)</label>
                <textarea
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  rows={2}
                  placeholder="Confirmation #, etc."
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setPaymentModal(null)}
                  className="flex-1 px-4 py-2 border rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => confirmToggle(paymentModal.id, true, paymentAmount, paymentNotes)}
                  disabled={!paymentAmount || !selectedBankAccount}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  Mark Paid
                </button>
              </div>
              
              <p className="text-xs text-gray-400 mt-3 text-center">
                Payment will be recorded and deducted from the selected account
              </p>
            </div>
          </div>
        )}

        {/* Add Bill Modal */}
        {addBillModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-96">
              <h3 className="font-semibold text-lg mb-4">Add New Bill</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Location *</label>
                <select
                  value={newBillLocationId}
                  onChange={(e) => setNewBillLocationId(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">Select location...</option>
                  {allLocations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Bill Name *</label>
                <input
                  type="text"
                  value={newBillName}
                  onChange={(e) => setNewBillName(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  placeholder="e.g., Electric, Water, Insurance"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Vendor/Provider</label>
                <input
                  type="text"
                  value={newBillVendor}
                  onChange={(e) => setNewBillVendor(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  placeholder="e.g., Toledo Edison, Columbia Gas"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Frequency</label>
                <select
                  value={newBillFrequency}
                  onChange={(e) => setNewBillFrequency(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="semi-annual">Semi-Annual</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setAddBillModal(false);
                    setNewBillLocationId('');
                    setNewBillName('');
                    setNewBillVendor('');
                    setNewBillFrequency('monthly');
                  }}
                  className="flex-1 px-4 py-2 border rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddBill}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Add Bill
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Close Month Modal - Old School Terminal Report */}
        {showCloseMonth && monthSummary && (() => {
          const totalCash = monthSummary.bankBalances?.reduce((sum, b) => sum + parseFloat(b.balance || 0), 0) || 0;
          const netWorth = totalCash + (monthSummary.inventoryValue || 0);
          const profitMargin = monthSummary.salesRevenue > 0 ? ((monthSummary.grossProfit / monthSummary.salesRevenue) * 100).toFixed(1) : 0;
          const inventoryTurnover = monthSummary.inventoryCount > 0 ? (monthSummary.vehiclesSold / monthSummary.inventoryCount).toFixed(2) : 0;
          
          const generateReportText = () => {
            const line = '='.repeat(60);
            const dline = '-'.repeat(60);
            const pad = (str, len = 40) => str.padEnd(len);
            const rpad = (str, len = 18) => str.toString().padStart(len);
            const fmt = (num) => '$' + (num || 0).toLocaleString();
            
            return `
${line}
    THE WHOLESALE CARS - END OF MONTH FINANCIAL REPORT
${line}
    Report Period: ${MONTHS[selectedMonth - 1]} ${selectedYear}
    Generated: ${new Date().toLocaleString()}
${line}

>>> PROFIT & LOSS STATEMENT
${dline}
${pad('Total Sales Revenue:')}${rpad(fmt(monthSummary.salesRevenue))}
${pad('Cost of Goods Sold:')}${rpad(fmt(monthSummary.purchaseCost))}
${pad('Gross Profit from Sales:')}${rpad(fmt(monthSummary.grossProfit))}
${pad('Profit Margin:')}${rpad(profitMargin + '%')}
${dline}
${pad('Operating Expenses:')}${rpad(fmt(monthSummary.totalExpenses))}
${pad('Bills & Utilities:')}${rpad(fmt(monthSummary.totalBillsPaid))}
${dline}
${pad('NET INCOME THIS MONTH:')}${rpad(fmt(monthSummary.grossProfit - monthSummary.totalExpenses - monthSummary.totalBillsPaid))}

>>> SALES PERFORMANCE
${dline}
${pad('Vehicles Sold:')}${rpad(monthSummary.vehiclesSold || 0)}
${pad('Total Revenue:')}${rpad(fmt(monthSummary.salesRevenue))}
${pad('Average Sale Price:')}${rpad(fmt(monthSummary.avgSalePrice))}
${pad('Average Profit per Vehicle:')}${rpad(fmt(monthSummary.avgProfit))}
${pad('Inventory Turnover Ratio:')}${rpad(inventoryTurnover)}

>>> INVENTORY STATUS
${dline}
${pad('Vehicles in Stock:')}${rpad(monthSummary.inventoryCount || 0)}
${pad('Total Inventory Value:')}${rpad(fmt(monthSummary.inventoryValue))}
${pad('Average Cost per Vehicle:')}${rpad(fmt(monthSummary.avgInventoryCost))}
${pad('Average Days on Lot:')}${rpad((monthSummary.avgDaysOnLot || 0) + ' days')}

>>> PURCHASING ACTIVITY
${dline}
${pad('Vehicles Purchased:')}${rpad(monthSummary.vehiclesPurchased || 0)}
${pad('Total Purchase Cost:')}${rpad(fmt(monthSummary.purchaseCost))}
${pad('Transport Costs:')}${rpad(fmt(monthSummary.transportCosts))}

>>> EXPENSE BREAKDOWN
${dline}
${pad('Bills & Utilities:')}${rpad(fmt(monthSummary.totalBillsPaid))}
${pad('Vehicle Expenses:')}${rpad(fmt(monthSummary.vehicleExpenses))}
${pad('Repair Orders:')}${rpad(fmt(monthSummary.repairCosts))}
${pad('Parts & Supplies:')}${rpad(fmt(monthSummary.partsCosts))}
${dline}
${pad('TOTAL EXPENSES:')}${rpad(fmt(monthSummary.totalExpenses + monthSummary.totalBillsPaid))}

>>> BANK ACCOUNT BALANCES
${dline}
${monthSummary.bankBalances?.map(b => pad(b.name + ':') + rpad(fmt(parseFloat(b.balance || 0)))).join('\n')}
${dline}
${pad('TOTAL CASH ON HAND:')}${rpad(fmt(totalCash))}

>>> BUSINESS NET WORTH
${dline}
${pad('Cash on Hand:')}${rpad(fmt(totalCash))}
${pad('Inventory Value:')}${rpad(fmt(monthSummary.inventoryValue))}
${line}
${pad('TOTAL NET WORTH:')}${rpad(fmt(netWorth))}
${line}

>>> KEY METRICS
${dline}
${pad('Profit Margin:')}${rpad(profitMargin + '%')}
${pad('Inventory Turnover:')}${rpad(inventoryTurnover)}
${pad('Avg Days to Sell:')}${rpad((monthSummary.avgDaysOnLot || 0) + ' days')}
${pad('Cash to Inventory Ratio:')}${rpad((totalCash / (monthSummary.inventoryValue || 1)).toFixed(2))}

${line}
                    END OF REPORT
${line}
`;
          };

          const downloadReport = () => {
            const text = generateReportText();
            const blob = new Blob([text], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `TWC_Monthly_Report_${MONTHS[selectedMonth - 1]}_${selectedYear}.txt`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          };

          return (
            <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50">
              <div className="bg-black border-2 border-green-500 rounded w-[700px] max-h-[90vh] overflow-hidden flex flex-col font-mono shadow-[0_0_20px_rgba(0,255,0,0.3)]">
                <div className="p-3 border-b border-green-500 flex items-center justify-between">
                  <div className="text-green-400 text-sm">
                    <span className="animate-pulse">_</span> LOTSENSE FINANCIAL TERMINAL v1.0
                  </div>
                  <button onClick={() => setShowCloseMonth(false)} className="text-green-500 hover:text-green-300">[ X ]</button>
                </div>
                
                <div className="flex-1 overflow-y-auto p-4 text-green-400 text-xs leading-relaxed whitespace-pre">
                  {generateReportText()}
                </div>

                <div className="p-3 border-t border-green-500 flex gap-3">
                  <button
                    onClick={() => setShowCloseMonth(false)}
                    className="flex-1 px-4 py-2 border border-green-500 text-green-400 hover:bg-green-900/30"
                  >
                    [ CANCEL ]
                  </button>
                  <button
                    onClick={downloadReport}
                    className="flex-1 px-4 py-2 border border-green-500 text-green-400 hover:bg-green-900/30"
                  >
                    [ DOWNLOAD REPORT ]
                  </button>
                  <button
                    onClick={() => { downloadReport(); confirmCloseMonth(); }}
                    className="flex-1 px-4 py-2 bg-green-600 text-black font-bold hover:bg-green-500"
                  >
                    [ CLOSE MONTH ]
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default BillPaymentChecklist;
